import { useMemo, type ReactNode } from 'react';
import {
  Globe,
  Terminal,
  LayoutGrid,
  MousePointerClick,
  Columns3,
  Settings,
  PanelRight,
  BadgeCheck,
  LayoutDashboard,
  Paperclip,
  Tag,
} from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { MonoBadge } from '@/components/shared/MonoBadge';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { collectPluginWebWiringIssues, type PluginManifest } from '@/lib/plugin-registry';
import { WebContributionRow } from './WebContributionRow';
import { ItemRow } from './ItemRow';
import { LucideIconByName } from './LucideIconByName';

interface ContributionDef {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  children: ReactNode;
}

export function PluginWebContributions({ plugin }: { plugin: PluginManifest }) {
  const web = plugin.web;
  const commands = web?.commands ?? [];
  const overviewCards = web?.overviewCards ?? [];
  const agentActions = web?.agentActions ?? [];
  const agentTabs = web?.agentTabs ?? [];
  const pageTabs = web?.pageTabs ?? [];
  const settingsSections = web?.settingsSections ?? [];
  const agentDetailSections = web?.agentDetailSections ?? [];
  const agentHeaderBadges = web?.agentHeaderBadges ?? [];
  const homeWidgets = web?.homeWidgets ?? [];
  const attachments = web?.attachments ?? [];
  const eventLabels = web?.eventLabels ?? [];

  const wiringIssues = useMemo(() => collectPluginWebWiringIssues(plugin), [plugin]);

  const contributions: ContributionDef[] = [
    {
      key: 'commands',
      icon: Terminal,
      label: `Commands (${commands.length})`,
      count: commands.length,
      children: commands.map((cmd) => (
        <ItemRow key={cmd.label}>
          <span className="font-medium">{cmd.label}</span>
          <MonoBadge>{cmd.to}</MonoBadge>
          <Badge variant="outline" className="text-3xs font-normal">
            {cmd.group}
          </Badge>
          <LucideIconByName name={cmd.icon} className="h-3.5 w-3.5 text-muted-foreground" />
        </ItemRow>
      )),
    },
    {
      key: 'overviewCards',
      icon: LayoutGrid,
      label: `Overview Cards (${overviewCards.length})`,
      count: overviewCards.length,
      children: overviewCards.map((card) => (
        <ItemRow key={card.label}>
          <div className="flex w-full flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{card.label}</span>
              <MonoBadge>{card.to}</MonoBadge>
              <Badge variant="outline" className="text-3xs font-normal">
                {card.group}
              </Badge>
            </div>
            <PageDescription className="mt-0 text-xs">{card.description}</PageDescription>
            {card.features.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {card.features.map((f) => (
                  <Badge key={f} variant="secondary" className="text-3xs font-normal">
                    {f}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </ItemRow>
      )),
    },
    {
      key: 'agentActions',
      icon: MousePointerClick,
      label: `Agent Actions (${agentActions.length})`,
      count: agentActions.length,
      children: agentActions.map((action) => (
        <ItemRow key={action.label}>
          <span className="font-medium">{action.label}</span>
          {action.to && <MonoBadge>{action.to}</MonoBadge>}
          {action.componentPath && (
            <Badge variant="outline" className="text-3xs font-normal">
              inline component
            </Badge>
          )}
          <LucideIconByName name={action.icon} className="h-3.5 w-3.5 text-muted-foreground" />
        </ItemRow>
      )),
    },
    {
      key: 'agentTabs',
      icon: Columns3,
      label: `Agent Tabs (${agentTabs.length})`,
      count: agentTabs.length,
      children: agentTabs.map((tab) => (
        <ItemRow key={tab.id}>
          <span className="font-medium">{tab.label}</span>
          <MonoBadge>{tab.id}</MonoBadge>
          <LucideIconByName name={tab.icon} className="h-3.5 w-3.5 text-muted-foreground" />
        </ItemRow>
      )),
    },
    {
      key: 'pageTabs',
      icon: Columns3,
      label: `Page Tabs (${pageTabs.length})`,
      count: pageTabs.length,
      children: pageTabs.map((tab) => (
        <ItemRow key={`${tab.slot}-${tab.id}`}>
          <span className="font-medium">{tab.label}</span>
          <MonoBadge>
            {tab.slot}/{tab.id}
          </MonoBadge>
          <LucideIconByName name={tab.icon} className="h-3.5 w-3.5 text-muted-foreground" />
          {tab.order != null && (
            <Badge variant="outline" className="text-3xs font-normal tabular-nums">
              order: {tab.order}
            </Badge>
          )}
        </ItemRow>
      )),
    },
    {
      key: 'settingsSections',
      icon: Settings,
      label: `Settings Sections (${settingsSections.length})`,
      count: settingsSections.length,
      children: settingsSections.map((section) => (
        <ItemRow key={section.id}>
          <span className="font-medium">{section.label}</span>
          <MonoBadge>{section.id}</MonoBadge>
          <LucideIconByName name={section.icon} className="h-3.5 w-3.5 text-muted-foreground" />
          {section.order != null && (
            <Badge variant="outline" className="text-3xs font-normal tabular-nums">
              order: {section.order}
            </Badge>
          )}
        </ItemRow>
      )),
    },
    {
      key: 'agentDetailSections',
      icon: PanelRight,
      label: `Agent Detail Sections (${agentDetailSections.length})`,
      count: agentDetailSections.length,
      children: agentDetailSections.map((section) => (
        <ItemRow key={section.id}>
          <span className="font-medium">{section.label}</span>
          <MonoBadge>{section.id}</MonoBadge>
          <LucideIconByName name={section.icon} className="h-3.5 w-3.5 text-muted-foreground" />
          {section.order != null && (
            <Badge variant="outline" className="text-3xs font-normal tabular-nums">
              order: {section.order}
            </Badge>
          )}
        </ItemRow>
      )),
    },
    {
      key: 'agentHeaderBadges',
      icon: BadgeCheck,
      label: `Agent Header Badges (${agentHeaderBadges.length})`,
      count: agentHeaderBadges.length,
      children: agentHeaderBadges.map((badge) => (
        <ItemRow key={badge.id}>
          <MonoBadge>{badge.id}</MonoBadge>
          {badge.order != null && (
            <Badge variant="outline" className="text-3xs font-normal tabular-nums">
              order: {badge.order}
            </Badge>
          )}
        </ItemRow>
      )),
    },
    {
      key: 'homeWidgets',
      icon: LayoutDashboard,
      label: `Home Widgets (${homeWidgets.length})`,
      count: homeWidgets.length,
      children: homeWidgets.map((widget) => (
        <ItemRow key={widget.id}>
          <MonoBadge>{widget.id}</MonoBadge>
          {widget.order != null && (
            <Badge variant="outline" className="text-3xs font-normal tabular-nums">
              order: {widget.order}
            </Badge>
          )}
        </ItemRow>
      )),
    },
    {
      key: 'attachments',
      icon: Paperclip,
      label: `Attachments (${attachments.length})`,
      count: attachments.length,
      children: attachments.map((att) => (
        <ItemRow key={att.id}>
          <span className="font-medium">{att.label}</span>
          <MonoBadge>{att.id}</MonoBadge>
          <LucideIconByName name={att.icon} className="h-3.5 w-3.5 text-muted-foreground" />
        </ItemRow>
      )),
    },
    {
      key: 'eventLabels',
      icon: Tag,
      label: `Event Labels (${eventLabels.length})`,
      count: eventLabels.length,
      children: eventLabels.map((el) => (
        <ItemRow key={el.type}>
          <MonoBadge>{el.type}</MonoBadge>
          <span className="font-medium">{el.label}</span>
          <Badge variant="outline" className="text-3xs font-normal">
            {el.style ?? 'system'}
          </Badge>
          <span className="text-xs text-muted-foreground">{el.tooltip}</span>
        </ItemRow>
      )),
    },
  ];

  const activeContributions = contributions.filter((c) => c.count > 0);

  if (activeContributions.length === 0 && wiringIssues.length === 0) return null;

  return (
    <section data-testid="plugin-detail-section-web-contributions">
      <IconSectionHeader icon={Globe}>Web UI Contributions</IconSectionHeader>
      {wiringIssues.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Web registration gaps</AlertTitle>
          <AlertDescription>
            <p className="text-xs">
              These manifest entries are not matched in any bundled plugin{' '}
              <code className="rounded bg-muted px-1">web/register.ts</code> map. UI slices that
              depend on them will be skipped at runtime.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
              {wiringIssues.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        {activeContributions.map((c) => (
          <WebContributionRow
            key={c.key}
            icon={c.icon}
            label={c.label}
            testId={`plugin-web-contribution-toggle-${c.key}`}
          >
            {c.children}
          </WebContributionRow>
        ))}
      </div>
    </section>
  );
}
